import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight, BookOpen, PenTool, Layers, FileDown, Store, NotebookPen, Wand2,
  Compass, Languages, GraduationCap, Sparkles, Globe2, Clapperboard, Radar, Mic,
  Boxes, Cpu, Database, ActivitySquare, ShieldCheck, Gauge,
} from 'lucide-react';
import axios from 'axios';

import { useSeoMeta } from '@/lib/useSeoMeta';
import PublicNavBar from '@/components/landing/PublicNavBar';
import StructuredData, { breadcrumbLd, webPageLd } from '@/components/seo/StructuredData';

/* 2026-10 — Public "What We Offer" page.
 * Content now DB-backed: GET /api/static/what-we-offer (seeded via
 * /app/backend/seeds/seed_static_content.py). Hardcoded arrays serve
 * as the fallback when the API hasn't been seeded yet. */

// Icon string → Lucide component map (covers all icons used in seed defaults)
const ICON_MAP = {
  PenTool, Wand2, Boxes, Layers, FileDown, Store, NotebookPen, Globe2, Clapperboard,
  Compass, ActivitySquare, Languages, Sparkles, ShieldCheck, GraduationCap, Mic, Radar,
  Cpu, Database, Gauge, BookOpen,
};

const _I = (name) => ICON_MAP[name] || BookOpen;  // resolve icon by name, fallback BookOpen

const TOOLS_FALLBACK = [
  { Icon: PenTool, title: 'Chapter Editor & Story Bible', body: "Draft chapter by chapter with a living Character Bible and Story Bible beside you, plus per-scene tone and pacing sliders so the model matches the mood you're writing toward." },
  { Icon: Wand2, title: 'Batch AI Polish & AI Assist', body: 'Ask the model to revise, extend, or tighten a single chapter — or the whole manuscript in one pass — with live "block X of Y" progress while it works.' },
  { Icon: Boxes, title: 'Novel Framework Builder', body: 'Reusable story templates: a bible, a cast of characters, locations, and a chapter-by-chapter outline you can apply to a brand-new book instead of starting from a blank page.' },
  { Icon: Layers, title: 'Synopsis → Framework Export', body: "Turn a finished manuscript's rolling summary into a fully genericized, reusable template — role archetypes and structural beats, no proper nouns — ready to seed your next book." },
  { Icon: FileDown, title: 'Export Suite', body: 'Download your manuscript as PDF, DOCX, EPUB, or plain text, formatted for query letters, beta readers, or direct KDP upload.' },
  { Icon: Store, title: 'Marketing & Publishing Panel', body: 'Push a finished book straight to the Crown & Rose Bookstore, manage ISBNs, and track royalties from one dashboard.' },
  { Icon: NotebookPen, title: "Writer's Notebook", body: 'Jot a scene idea on your phone the moment it hits you — it syncs straight into the right project the next time you open The Loom on desktop.' },
  { Icon: Globe2, title: 'World Builder', body: "A dedicated worldbuilding tier inside your Story Bible — lore, locations, rules of your setting — living next to your characters and arc so your world stays consistent book after book." },
  { Icon: Clapperboard, title: 'Story → Script Conversion with Live Editing', body: "Convert a finished novel into a stage play, screenplay, or radio play in Fountain format, then edit it live right in the browser." },
];

const AIDS_FALLBACK = [
  { Icon: Compass, title: 'Developmental Audit', body: 'An AI pass that flags plot holes, pacing dips, and character inconsistencies before you query agents or publish.' },
  { Icon: ActivitySquare, title: 'Chapter Continuity Checks', body: 'Catches contradictions across chapters automatically.' },
  { Icon: Languages, title: 'Curated Language Choice', body: 'Work inside The Loom in your own language: English, Deutsch, Español, Français, Português, or 中文.' },
  { Icon: Sparkles, title: 'Starter Template Gallery', body: 'Genre-specific frameworks to get a first draft moving in minutes.' },
  { Icon: ShieldCheck, title: 'OHAS D3 Human-Authorship Guidance', body: 'Built-in guardrails and documentation for the Open Human Authorship Standard.' },
  { Icon: GraduationCap, title: 'Guided Onboarding & Training Center', body: "Walkthroughs for first-time authors." },
  { Icon: Mic, title: 'Professional Author Advice (Voice Coach)', body: "An AI coach audits your narrative voice and each character's dialogue against your Story Bible." },
  { Icon: Radar, title: 'Character Drift Analysis', body: "Flags dialogue drift as low, moderate, or severe with concrete examples plus a fix." },
];

const BACKEND_FALLBACK = [
  { Icon: Cpu, title: 'Block-Scaled Generation', body: 'Long manuscripts are automatically split into right-sized blocks.' },
  { Icon: Boxes, title: 'Mixed-LLM Continuity', body: 'Switch between Claude, GPT, and Gemini mid-manuscript with rolling-summary continuity.' },
  { Icon: Database, title: 'Managed Context Caching', body: 'The Loom tracks what the model knows and only sends what changed.' },
  { Icon: Gauge, title: 'Stalled-Job Auto-Recovery', body: 'Any AI job that stalls mid-run is automatically flagged and recoverable.' },
  { Icon: ActivitySquare, title: 'Live Progress Tracking', body: 'Long AI passes show real-time progress instead of a spinner.' },
  { Icon: ShieldCheck, title: 'Secure, Stripe-Managed Billing', body: 'Crown & Rose never stores your card.' },
  { Icon: Gauge, title: 'Per-Project Budget Guardrails', body: 'Spend limits and usage tracking keep AI costs predictable.' },
];

function FeatureGrid({ items, testidPrefix }) {
  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2" data-testid={`${testidPrefix}-grid`}>
      {items.map(({ Icon, icon, title, body }, i) => {
        const IconComp = Icon || _I(icon);
        return (
          <article
            key={title}
            className="rounded-lg border border-border bg-card p-5"
            data-testid={`${testidPrefix}-item-${i}`}
          >
            <div className="mb-2 flex items-center gap-2">
              <IconComp className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <h3 className="text-base font-semibold leading-snug">{title}</h3>
            </div>
            <p className="text-sm leading-relaxed text-muted-foreground">{body}</p>
          </article>
        );
      })}
    </div>
  );
}

export default function WhatWeOfferPage() {
  const [cmsContent, setCmsContent] = useState(null);
  const [cmsLoaded,  setCmsLoaded]  = useState(false);

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    axios.get(`${base}/api/static/what-we-offer`)
      .then(({ data }) => { setCmsContent(data); })
      .catch(() => { /* fall through to hardcoded defaults */ })
      .finally(() => setCmsLoaded(true));
  }, []);

  const c = cmsContent?.content || {};
  const seo = cmsContent?.seo || {};

  // Resolve feature arrays: DB items (icon string) OR hardcoded fallback (Icon component)
  const tools   = (c.tools?.length)            ? c.tools           : TOOLS_FALLBACK;
  const aids    = (c.aids?.length)             ? c.aids            : AIDS_FALLBACK;
  const backend = (c.backend_features?.length) ? c.backend_features : BACKEND_FALLBACK;

  useSeoMeta('/what-we-offer', {
    title:       seo.title       || 'What We Offer — Tools, Aids & Backend Support | Crown & Rose',
    description: seo.description || "Every feature inside The Loom, Crown & Rose's AI novel-writing studio.",
    keywords:    seo.keywords    || 'The Loom features, AI novel writing tools, indie author software',
    og_image:    seo.og_image    || 'https://static.prod-images.emergentagent.com/jobs/67d45115-9fac-4a11-82ed-b7a8bff08f0b/images/937ebcf7da8dc859e9e433c164ed04fe9e3279e7e7a1317aa2ceb9b8f15978df.jpeg',
  });
  const navigate = useNavigate();
  const siteUrl  = typeof window !== 'undefined' ? window.location.origin : 'https://crownnrose.com';

  return (
    <div className="h-full overflow-y-auto bg-background text-foreground" data-testid="what-we-offer-page">
      <StructuredData id="wwo-breadcrumb" data={breadcrumbLd([
        { name: 'Home',           item: `${siteUrl}/` },
        { name: 'What We Offer',  item: `${siteUrl}/what-we-offer` },
      ])} />
      <StructuredData id="wwo-page" data={webPageLd({
        siteUrl,
        path:        '/what-we-offer',
        name:        seo.title || 'What We Offer — Tools, Aids & Backend Support | Crown & Rose',
        description: seo.description || "Every feature inside The Loom, Crown & Rose's AI novel-writing studio.",
      })} />


      <PublicNavBar
        onSignIn={() => navigate('/login')}
        onSignUp={() => navigate('/login?mode=signup')}
      />

      <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight" data-testid="what-we-offer-title">
          What We Offer
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground" data-testid="what-we-offer-intro">
          {c.page_intro || (
            <>
              The Loom is Crown &amp; Rose's AI-assisted novel-writing studio. Everything below
              is organized into three groups: the <strong>Tools</strong> you reach for directly,
              the <strong>Aids</strong> that guide your craft without writing for you, and the{' '}
              <strong>Backend Support Features</strong> that quietly keep long manuscripts running
              — so you never have to think about them at all.
            </>
          )}
        </p>

        <section className="mt-10" data-testid="what-we-offer-who-we-are-section" aria-labelledby="wwo-who-heading">
          <h2 id="wwo-who-heading" className="text-2xl font-semibold tracking-tight">Who We Are</h2>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground" data-testid="what-we-offer-who-we-are-body">
            {c.who_we_are_body || "Crown & Rose is a publishing suite built for independent novelists who want to write, finish, and sell their own books without handing control to a traditional house."}
          </p>
        </section>

        <section className="mt-12" data-testid="what-we-offer-tools-section" aria-labelledby="wwo-tools-heading">
          <h2 id="wwo-tools-heading" className="text-2xl font-semibold tracking-tight">Tools</h2>
          <p className="mt-1 text-sm text-muted-foreground">{c.tools_section_desc || 'The instruments you pick up and use every writing session.'}</p>
          <div className="mt-5"><FeatureGrid items={tools} testidPrefix="wwo-tools" /></div>
        </section>

        <section className="mt-12" data-testid="what-we-offer-aids-section" aria-labelledby="wwo-aids-heading">
          <h2 id="wwo-aids-heading" className="text-2xl font-semibold tracking-tight">Aids</h2>
          <p className="mt-1 text-sm text-muted-foreground">{c.aids_section_desc || 'Guidance and guardrails that sharpen your craft without taking the pen from your hand.'}</p>
          <div className="mt-5"><FeatureGrid items={aids} testidPrefix="wwo-aids" /></div>
        </section>

        <section className="mt-12" data-testid="what-we-offer-backend-section" aria-labelledby="wwo-backend-heading">
          <h2 id="wwo-backend-heading" className="text-2xl font-semibold tracking-tight">Backend Support Features</h2>
          <p className="mt-1 text-sm text-muted-foreground">{c.backend_section_desc || "Infrastructure that runs underneath every session so your model doesn't stall."}</p>
          <div className="mt-5"><FeatureGrid items={backend} testidPrefix="wwo-backend" /></div>
        </section>

        <div className="mt-14 flex flex-wrap items-center gap-3 border-t border-border pt-8">
          <Link
            to="/how-we-help-writers"
            data-testid="what-we-offer-cta-how-we-help"
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
          >
            See how we help writers build a business <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <Link
            to="/faq"
            data-testid="what-we-offer-cta-faq"
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-4 py-2 text-sm font-semibold hover:border-primary hover:text-primary"
          >
            Read the FAQ
          </Link>
          <Link
            to="/blog"
            data-testid="what-we-offer-cta-blog"
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-4 py-2 text-sm font-semibold hover:border-primary hover:text-primary"
          >
            Read the blog
          </Link>
        </div>
      </main>
    </div>
  );
}
